/**
 * Settings → AI's image model (slice 2f spec §9), pure: which models it can
 * pick from, the edits, and the checks the server makes. `image-model.tsx`
 * only renders these.
 */
import type { Me } from "../../app/me";
import { modelLabel, type Catalog } from "../setup/onboarding";
import { fallbackChoices, modelOptions, routingOf, type ModelOptionGroup } from "./ai-view";

type Ai = NonNullable<Me["computer"]>["ai"];

/** The image model and its fallbacks, in order; null for none. */
export type ImageRouting = { model: string; fallbacks: string[] } | null;

/** At most this many image fallbacks (slice 2f spec §9). */
export const MAX_IMAGE_FALLBACKS = 2;

export const imageRoutingOf = (ai: Ai): ImageRouting => ai.image ?? null;

/** The models it can pick from: the connected providers' that take images. */
export function imageOptions(catalog: Catalog | undefined, ai: Ai): ModelOptionGroup[] {
  return modelOptions(catalog, ai)
    .map((g) => ({ ...g, models: g.models.filter((m) => m.image) }))
    .filter((g) => g.models.length > 0);
}

/** The image models Add a fallback… offers: any the image routing doesn't use. */
export function imageFallbackChoices(options: ModelOptionGroup[], image: ImageRouting) {
  return fallbackChoices(options, image ?? { model: null, fallbacks: [] });
}

/** A new image model from the select: out of the fallbacks if it was one. */
export function setImageModel(image: ImageRouting, model: string): ImageRouting {
  return { model, fallbacks: (image?.fallbacks ?? []).filter((f) => f !== model) };
}

export function addImageFallback(image: ImageRouting, model: string): ImageRouting {
  return image ? { ...image, fallbacks: [...image.fallbacks, model] } : { model, fallbacks: [] };
}

export function removeImageFallback(image: ImageRouting, index: number): ImageRouting {
  return image ? { ...image, fallbacks: image.fallbacks.filter((_, i) => i !== index) } : null;
}

/** The server's checks before it's sent (`invalid_routing`, `invalid_model`), in its words. */
export function imageProblem(image: ImageRouting, ai: Ai, catalog: Catalog | undefined) {
  if (!image) return null;
  if (!routingOf(ai).model) return "Pick a default model first.";
  if (image.fallbacks.length > MAX_IMAGE_FALLBACKS) return "Up to two image fallbacks.";
  const all = [image.model, ...image.fallbacks];
  if (new Set(all).size !== all.length) return "An image model can be in the list only once.";
  const offered = new Set(imageOptions(catalog, ai).flatMap((g) => g.models.map((m) => m.id)));
  const stranded = all.find((m) => !offered.has(m) && !offeredBefore(ai, m));
  if (!stranded) return null;
  return `${modelLabel(catalog, stranded) ?? stranded} can't look at images, or needs a connected account.`;
}

/** A model already saved may stay where it is (the server's rule). */
function offeredBefore(ai: Ai, model: string): boolean {
  return ai.image ? [ai.image.model, ...ai.image.fallbacks].includes(model) : false;
}

/** Whether a model takes images itself (slice 2f §9): then it looks at photos with no image model. */
export function seesImages(catalog: Catalog | undefined, model: string | null): boolean {
  if (!catalog || !model) return false;
  return Object.values(catalog).some((ms) => ms.some((m) => m.id === model && m.image === true));
}
